import { getCookie } from 'helpers/index';

/**
 * Phase 4e — the data layer of the **sizer selection popup**
 * (`django/static/mvc/header/view/sizer_popup.js`), i.e. the six GETs it uses to
 * populate itself and the THREE `sizer_apply` POSTs that are the only server
 * writes in the entire `/sizer/` feature (discovery §4b).
 *
 * ★ WHY PLAIN `fetch` AND NOT RTK QUERY — the same reasoning `sizerApi.ts`
 * records for `postSizerMatrixData`, plus two popup-specific reasons:
 *
 *  1. The popup's loaders are ORDER- and VERSION-sensitive by design. Legacy
 *     guards every one of them with a monotonically-increasing counter
 *     (`__ahusVersion` / `__tunnelsVersion` / `__sectionsVersion`,
 *     sizer_popup.js:118-120, :1034-1041, :1065-1084) and DISCARDS a resolved
 *     response whose counter has moved on. That is exactly the race RTK Query's
 *     cache would paper over with a different (and observably different) answer.
 *  2. The whole result set is persisted into ONE `localStorage` bag
 *     (`sizer_popup_fields_status`) that the still-live Backbone header reads on
 *     every non-React page — so the cache of record is localStorage, not the
 *     store. A second cache in front of it can only desynchronise the two.
 *
 * Both helpers are ports of `fn.uni_tools.get` / `fn.uni_tools.post`
 * (`django/static/mvc/universal_fn/universal_fn_tools.js:133-171` / `:240-280`),
 * including their retry and error semantics — see each docblock.
 */

/** `fn.uni_tools.standardizeURL` (universal_fn_tools.js:118-126): append a
 * trailing slash to the PATH when it neither ends in `/` nor in a digit. Every
 * URL below already complies; ported so a future caller cannot drift. */
function standardizeUrl(url: string): string {
  const parts = url.split('?');
  const path = parts[0];
  const last = path[path.length - 1];
  if (last !== '/' && Number.isNaN(Number(last))) parts[0] = `${path}/`;
  return parts.join('?');
}

/**
 * ⚠ The legacy helpers put `credentials: 'include'` INSIDE the `headers` object
 * (universal_fn_tools.js:150 / :259) — its own comment ("Or maybe same-origin")
 * shows it was meant as a fetch option. As written it is a junk request header
 * and the request runs on fetch's DEFAULT `same-origin` credentials mode. We
 * send the EFFECTIVE behaviour (`credentials: 'same-origin'`) and drop the junk
 * header: it has no server-side reader (grepped) and reproducing it would put a
 * meaningless header on every request for zero observable gain.
 */
function legacyHeaders(): HeadersInit {
  return {
    'Content-type': 'application/json',
    'X-CSRFToken': getCookie('csrftoken') ?? '',
    'X-Requested-With': 'XMLHttpRequest',
  };
}

const RETRIES = 3;

/**
 * `fn.uni_tools.get` (universal_fn_tools.js:133-171), verbatim semantics:
 *  - up to 3 attempts, but ONLY a 500 retries (`if (!resp.ok) { if (500 && more
 *    attempts) continue; }` — note there is no `else`, so a 400/404 falls
 *    straight through to `resp.json()`);
 *  - a 401 navigates to `/login` and the promise never settles;
 *  - a 204 returns the Response object itself (callers here never see one);
 *  - anything else returns `resp.json()`.
 * Exhausting the loop returns `undefined` — legacy's function simply falls off
 * the end. Kept, because `updateProjects` genuinely relies on an `undefined`
 * return elsewhere (see `loadEditableProjects`).
 */
export async function sizerPopupGet<T>(url: string): Promise<T | undefined> {
  const target = standardizeUrl(url);
  for (let i = 0; i < RETRIES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(target, {
      method: 'GET',
      credentials: 'same-origin',
      headers: legacyHeaders(),
    });
    if (!response.ok && response.status === 500 && i < RETRIES - 1) continue;
    if (response.status === 401) {
      window.location.href = '/login';
      return new Promise<T>(() => {});
    }
    if (response.status === 204) return undefined;
    // eslint-disable-next-line no-await-in-loop
    return (await response.json()) as T;
  }
  return undefined;
}

/**
 * `fn.uni_tools.post` (universal_fn_tools.js:240-280).
 *
 * ★★ THE QUIRK THAT MATTERS FOR APPLY: the body is parsed inside a `try`, and
 * the `catch` only fires when `resp.json()` ITSELF throws. A DRF validation
 * error (400) has a perfectly good JSON body, so it parses and is RETURNED as a
 * normal result — `apply()` never learns the write failed and still shows
 * "Applied…". Only a non-JSON response (a 500 HTML error page, a network drop)
 * reaches the throw. Reproduced exactly; do NOT add a `response.ok` check.
 */
export async function sizerPopupPost<T>(url: string, data: unknown): Promise<T | undefined> {
  const target = standardizeUrl(url);
  for (let i = 0; i < RETRIES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(target, {
      method: 'POST',
      credentials: 'same-origin',
      headers: legacyHeaders(),
      body: JSON.stringify(data),
    });
    try {
      // eslint-disable-next-line no-await-in-loop
      return (await response.json()) as T;
    } catch {
      if (response.status === 500 && i < RETRIES - 1) continue;
      if (response.status === 401) {
        window.location.href = '/login';
        return new Promise<T>(() => {});
      }
      throw new Error(`Invalid response with code ${response.status}`);
    }
  }
  return undefined;
}

/**
 * `fn.uni_tools.serialize` (universal_fn_tools.js:807-821) — project a list of
 * rows onto a fixed key set, `[]` for a falsy input. The popup runs EVERY list
 * through it, which is why `fieldsStatus` only ever stores those narrow shapes.
 */
export function serializeRows<T extends object, K extends keyof T>(
  rows: T[] | undefined | null,
  keys: readonly K[]
): Pick<T, K>[] {
  if (!rows) return [];
  return rows.map((row) => {
    const out = {} as Pick<T, K>;
    for (const key of keys) out[key] = row[key];
    return out;
  });
}

// ---------------------------------------------------------------------------
// row shapes
// ---------------------------------------------------------------------------

/** `serialize(projects, ['id','name'])` — sizer_popup.js:1104-1116. */
export interface ISizerPopupProject {
  id: number;
  name: string;
}

/** `serialize(units_sizer, ['id','tag'])` — sizer_popup.js:1123-1128. */
export interface ISizerPopupAhu {
  id: number;
  tag: string;
}

/** `serialize(tunnels, ['id','index_id','width','height'])` — :1134-1137. */
export interface ISizerPopupTunnel {
  id: number;
  index_id: number;
  width: number;
  height: number;
}

/** `serialize(sections, ['id','name','type','tag','tunnel_id'])` — :1143-1148.
 * `type` is the section's component type: `coil` | `filter` | `fan` | … */
export interface ISizerPopupSection {
  id: number;
  name: string;
  type: string;
  tag: string | null;
  tunnel_id: number;
}

/** `GET /api/v1.0/master/v1.0.0/ahu/tunnel_indexes/` (`fn['tunnel.indexes.master']`,
 * universal_map.js:101-105) — `{id, name}` rows, e.g. `1:1`. */
export interface ISizerPopupTunnelIndex {
  id: number;
  name: string;
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// loaders
// ---------------------------------------------------------------------------

/**
 * `__loadPinnedProjects` (sizer_popup.js:1101-1108).
 *
 * ⚠ TWO quirks kept verbatim:
 *  - the guard `if (!localStorage.getItem("sizer_popup")) return;` returns
 *    `undefined` (NOT `[]`), which is why a fresh page load fires no project
 *    request at all and the lists only load on the first popup OPEN — the
 *    behaviour discovery OBSERVED (§7b: the extra GETs appear "on open").
 *  - the query param is the TRIPLE-N `pinnned=true` (a backend typo the React
 *    dashboard already mirrors — `dashboardApi.ts:136`).
 */
export async function loadPinnedProjects(name?: string): Promise<ISizerPopupProject[] | undefined> {
  if (!window.localStorage.getItem('sizer_popup')) return undefined;
  const rows = await sizerPopupGet<ISizerPopupProject[]>(
    `/api/v1.0/projects/?pinnned=true${name ? `&name=${name}` : ''}`
  );
  return serializeRows(rows, ['id', 'name'] as const);
}

/** `__loadEditableProjects` (sizer_popup.js:1110-1117) — same `sizer_popup`
 * guard; page-paginated (100/page, see `scrollProjects`). */
export async function loadEditableProjects(
  page = 1,
  name?: string
): Promise<ISizerPopupProject[] | undefined> {
  if (!window.localStorage.getItem('sizer_popup')) return undefined;
  const rows = await sizerPopupGet<ISizerPopupProject[]>(
    `/api/v1.0/projects/?page=${page}${name ? `&name=${name}` : ''}`
  );
  return serializeRows(rows, ['id', 'name'] as const);
}

/** `__loadAHUsByProjectID` (:1120-1129) — note the SIZER-SPECIFIC list endpoint
 * `units_sizer` (only AHUs the user may write), not `ahu/units`. */
export async function loadAhusByProjectId(projectId: unknown): Promise<ISizerPopupAhu[]> {
  if (!projectId) return [];
  const rows = await sizerPopupGet<ISizerPopupAhu[]>(
    `/api/v1.0/ahu/units_sizer/?project_id=${projectId}`
  );
  return serializeRows(rows, ['id', 'tag'] as const);
}

/** `__loadTunnelsByAHUID` (:1131-1138). */
export async function loadTunnelsByAhuId(ahuId: unknown): Promise<ISizerPopupTunnel[]> {
  if (!ahuId) return [];
  const rows = await sizerPopupGet<ISizerPopupTunnel[]>(`/api/v1.0/ahu/tunnels/?ahu_id=${ahuId}`);
  return serializeRows(rows, ['id', 'index_id', 'width', 'height'] as const);
}

/** `__loadSectionsByTunnelID` (:1140-1149). */
export async function loadSectionsByTunnelId(tunnelId: unknown): Promise<ISizerPopupSection[]> {
  if (!tunnelId) return [];
  const rows = await sizerPopupGet<ISizerPopupSection[]>(
    `/api/v1.0/ahu/sections/?tunnel_id=${tunnelId}`
  );
  return serializeRows(rows, ['id', 'name', 'type', 'tag', 'tunnel_id'] as const);
}

/**
 * `fn['tunnel.indexes.master']` (universal_map.js:101-105) — a `Promise.lazy`,
 * i.e. fetched at most ONCE per page and shared by every awaiter. The
 * module-level promise below is that laziness.
 */
let tunnelIndexesPromise: Promise<ISizerPopupTunnelIndex[]> | null = null;
export function loadTunnelIndexes(): Promise<ISizerPopupTunnelIndex[]> {
  if (!tunnelIndexesPromise) {
    tunnelIndexesPromise = sizerPopupGet<ISizerPopupTunnelIndex[]>(
      '/api/v1.0/master/v1.0.0/ahu/tunnel_indexes/'
    ).then((rows) => rows ?? []);
  }
  return tunnelIndexesPromise;
}

// ---------------------------------------------------------------------------
// ★ THE WRITES — discovery §4b. All three are permission-gated server-side with
// `@PermissionsValidator.for_ahu(activity=Activities.Write,
//   required_permissions=['ahu_board_item.ahu'])`.
// ---------------------------------------------------------------------------

/**
 * `POST /api/v1.0/ahu/units/{ahu_id}/sizer_apply/` — sizer_popup.js:863-866,
 * backend `api/rest_ahu/ahu_viewset.py:436-449`, route `urls.py:120`.
 *
 * ⚠ `airflow_actual` IS SENT AND IS SILENTLY DISCARDED. It is not a field on
 * `AHUUnitSerializer`, so DRF drops it and only `construction_class_id`
 * persists (discovery §4b + §9 "Asks for Vicken" #3, confirmed live: request
 * carried `airflow_actual: 20000`, response `{"done": true}`, nothing stored).
 * That is a BACKEND defect on Vicken's list — the port sends the request
 * exactly as legacy sends it and does NOT work around it.
 */
export function applyUnitSizer(
  ahuId: unknown,
  body: { construction_class_id: number; airflow_actual: number | string }
): Promise<{ done?: boolean } | undefined> {
  return sizerPopupPost(`/api/v1.0/ahu/units/${ahuId}/sizer_apply/`, body);
}

/**
 * `POST /api/v1.0/ahu/tunnels/{tunnel_id}/sizer_apply/` — sizer_popup.js:869-873,
 * backend `api/rest_ahu/tunnel_viewset.py:290-326`, route `urls.py:175`.
 * `width`/`height` are the cell's STRINGS (`"84.00"`), straight off the matrix
 * response — not numbers. The backend pops `casing_insulation_id` and applies it
 * to every section of the tunnel first, then saves the tunnel dimensions.
 */
export function applyTunnelSizer(
  tunnelId: unknown,
  body: { width: unknown; height: unknown; casing_insulation_id: number | null }
): Promise<{ done?: boolean } | undefined> {
  return sizerPopupPost(`/api/v1.0/ahu/tunnels/${tunnelId}/sizer_apply/`, body);
}

/**
 * `POST /api/v1.0/ahu/sections/{section_id}/sizer_apply/` — sizer_popup.js:832-836
 * + :877-884, backend `api/rest_ahu/section_viewset.py:640-716`, route
 * `urls.py:199-201`. Body is `{component, ...dataGetter output}`; the getters
 * are `buildCoilApplyData` / `buildFilterApplyData` in the controller.
 */
export function applySectionSizer(
  sectionId: unknown,
  body: Record<string, unknown>
): Promise<Record<string, unknown> | undefined> {
  return sizerPopupPost(`/api/v1.0/ahu/sections/${sectionId}/sizer_apply/`, body);
}
